import { useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ORG_REF } from "@/data/entity";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import { CTAFinal } from "@/components/home/CTAFinal";
import { Button } from "@/components/ui/button";
import { houstonLocationData } from "@/data/locationPagesData";
import { LocalSEOCases } from "@/components/local-seo/LocalSEOCases";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Calendar,
  MapPin,
  CheckCircle2,
  Target,
  AlertTriangle,
  Lightbulb,
  FileText,
  HelpCircle
} from "lucide-react";

type Language = 'es' | 'en';

const LocalSEOHoustonPage = () => {
  // Idioma derivado de la URL: /seo-local-houston (ES) y
  // /en/local-seo-houston (EN) sirven contenido distinto, no el mismo.
  const { language, url, onLanguageChange } = usePageSeo();
  const data = houstonLocationData;

  const title = language === 'es' ? data.titleEs : data.titleEn;
  const description = language === 'es' ? data.descriptionEs : data.descriptionEn;
  const keywords = language === 'es' ? data.keywordsEs : data.keywordsEn;
  const heroTitle = language === 'es' ? data.heroTitleEs : data.heroTitleEn;
  const heroSubtitle = language === 'es' ? data.heroSubtitleEs : data.heroSubtitleEn;
  const whyLocal = language === 'es' ? data.whyLocalEs : data.whyLocalEn;
  const challenges = language === 'es' ? data.localChallengesEs : data.localChallengesEn;
  const services = language === 'es' ? data.servicesEs : data.servicesEn;
  const faqs = language === 'es' ? data.faqsEs : data.faqsEn;

  const labels = {
    es: {
      badge: "SEO Local Houston",
      cta: "Solicitar reunión inicial gratuita de 30 minutos",
      ctaNote: "30 minutos · Sin compromiso",
      diagnosticLink: "/diagnostico-seo-gratis",
      hubLink: "/seo-local",
      backToHub: "← Volver a SEO Local",
      whyTitle: "Por qué el SEO local en Houston es diferente",
      challengesTitle: "Retos del mercado de Houston",
      servicesTitle: "Qué hago como consultor SEO local en Houston",
      methodologyTitle: "Metodología local",
      methodologySteps: [
        { title: "Diagnóstico local", description: "Análisis de tu visibilidad actual en Houston, competidores locales y oportunidades." },
        { title: "Optimización técnica", description: "Corrección de errores técnicos y optimización para búsquedas locales." },
        { title: "Contenido local", description: "Páginas de servicio geolocalizado para Houston y áreas de servicio." },
        { title: "Autoridad local", description: "Citaciones NAP, directorios y estrategia de reseñas." },
        { title: "Medición", description: "Seguimiento de posiciones locales, tráfico y leads desde Houston." }
      ],
      forWhoTitle: "Para quién es / Para quién no es",
      idealFor: "Ideal para:",
      idealItems: [
        "Empresas B2B que atienden el área de Houston",
        "Fabricantes, distribuidores y proveedores de servicios",
        "Negocios que quieren diversificar más allá de referencias",
        "Empresas que invierten en crecimiento real"
      ],
      notFor: "No es para:",
      notItems: [
        "Negocios sin presencia real en el área de Houston",
        "Empresas que buscan resultados inmediatos sin proceso",
        "Proyectos sin presupuesto de marketing"
      ],
      faqTitle: "Preguntas frecuentes sobre SEO local en Houston"
    },
    en: {
      badge: "Houston Local SEO",
      cta: "Request free 30-minute initial consultation",
      ctaNote: "30 minutes · No commitment",
      diagnosticLink: "/en/free-seo-diagnosis",
      hubLink: "/local-seo",
      backToHub: "← Back to Local SEO",
      whyTitle: "Why local SEO in Houston is different",
      challengesTitle: "Houston market challenges",
      servicesTitle: "What I do as a local SEO consultant in Houston",
      methodologyTitle: "Local methodology",
      methodologySteps: [
        { title: "Local diagnosis", description: "Analysis of your current visibility in Houston, local competitors and opportunities." },
        { title: "Technical optimization", description: "Technical error correction and optimization for local searches." },
        { title: "Local content", description: "Geolocated service pages for Houston and service areas." },
        { title: "Local authority", description: "NAP citations, directories and review strategy." },
        { title: "Measurement", description: "Tracking of local positions, traffic and leads from Houston." }
      ],
      forWhoTitle: "Who it's for / Who it's not for",
      idealFor: "Ideal for:",
      idealItems: [
        "B2B companies serving the Houston area",
        "Manufacturers, distributors and service providers",
        "Businesses that want to diversify beyond referrals",
        "Companies that invest in real growth"
      ],
      notFor: "Not for:",
      notItems: [
        "Businesses without real presence in the Houston area",
        "Companies seeking immediate results without process",
        "Projects without marketing budget"
      ],
      faqTitle: "Frequently asked questions about local SEO in Houston"
    }
  };

  const t = labels[language];

  // Service con área de servicio Houston. No se declara una LocalBusiness
  // propia: la sede física única está en Shenandoah, TX.
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": language === 'es' ? "Consultoría de SEO Local en Houston" : "Local SEO Consulting in Houston",
    "description": description,
    "url": `https://luisroc.com/${language === 'es' ? data.slug : data.slugEn}`,
    "serviceType": "Local SEO",
    "provider": ORG_REF,
    "areaServed": {
      "@type": "City",
      "name": data.city
    },
  };


  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <SEOWrapper
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      language={language}
      pageType="website"
      summary={description}
      keyPoints={keywords.slice(0, 5)}
    >
      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          {/* SECTION 1: Hero Local */}
          <section className="section-padding bg-background">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                {/* Back link */}
                <Link 
                  to={t.hubLink}
                  className="inline-flex items-center text-sm text-muted-foreground hover:text-brand mb-6 transition-colors"
                >
                  {t.backToHub}
                </Link>
                
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 rounded-full text-brand text-sm font-medium mb-6">
                  <MapPin className="w-4 h-4" />
                  {t.badge}
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                  {heroTitle}
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl">
                  {heroSubtitle}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Button 
                    size="lg" 
                    className="btn-gradient group text-base px-8 py-6"
                    asChild
                  >
                    <Link to={t.diagnosticLink}>
                      <Calendar className="mr-2 h-5 w-5" />
                      {t.cta}
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {t.ctaNote}
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 2: Why Local SEO in Houston */}
          <section className="section-padding bg-secondary/30">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="text-2xl font-bold">{t.whyTitle}</h2>
                </div>
                
                <p className="text-foreground leading-relaxed mb-8">
                  {whyLocal}
                </p>
                
                <div className="bg-background rounded-xl p-6 border border-border">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-brand" />
                    {t.challengesTitle}
                  </h3>
                  <ul className="space-y-3">
                    {challenges.map((challenge, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3: What I Do */}
          <section className="section-padding bg-background">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="text-2xl font-bold">{t.servicesTitle}</h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {services.map((service, index) => (
                    <div 
                      key={index}
                      className="bg-secondary/30 rounded-xl p-6 border border-border"
                    >
                      <h3 className="text-lg font-semibold mb-3 text-brand">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {service.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 4: Methodology */}
          <section className="section-padding bg-secondary/30">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="text-2xl font-bold">{t.methodologyTitle}</h2>
                </div>
                
                <div className="space-y-0">
                  {t.methodologySteps.map((step, index) => (
                    <div 
                      key={index}
                      className="relative flex gap-6 pb-8 last:pb-0"
                    >
                      {index < t.methodologySteps.length - 1 && (
                        <div className="absolute left-6 top-14 w-0.5 h-[calc(100%-3rem)] bg-border" />
                      )}
                      
                      <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      
                      <div className="flex-1 pt-2">
                        <h3 className="text-lg font-semibold mb-2">
                          {step.title}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 5: Cases */}
          <LocalSEOCases language={language} />

          {/* SECTION 6: For Who */}
          <section className="section-padding bg-background">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-8 text-center">{t.forWhoTitle}</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-brand/5 rounded-2xl p-6 border border-brand/20">
                    <h3 className="font-semibold mb-4 text-brand">{t.idealFor}</h3>
                    <ul className="space-y-3">
                      {t.idealItems.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm">
                          <CheckCircle2 className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-secondary/30 rounded-2xl p-6 border border-border">
                    <h3 className="font-semibold mb-4 text-muted-foreground">{t.notFor}</h3>
                    <ul className="space-y-3">
                      {t.notItems.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 7: FAQ */}
          <section className="section-padding bg-secondary/30">
            <div className="container-custom">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 rounded-full text-brand text-sm font-medium mb-4">
                    <HelpCircle className="w-4 h-4" />
                    FAQ
                  </div>
                  <h2 className="text-2xl font-bold">
                    {t.faqTitle}
                  </h2>
                </div>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left font-semibold">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>

          {/* SECTION 8: CTA Final */}
          <CTAFinal language={language} />
        </main>

        <Footer language={language} />
        <WhatsAppFloat />
      </div>
      
      {/* Schemas */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </SEOWrapper>
  );
};

export default LocalSEOHoustonPage;
